// Rotating announcement bar (<announcement-bar>).
// One message is visible at a time on every screen; the arrows step through
// them and, when the section asks for it, a timer advances on its own.
// Without JS the first message stays on screen, which is why the markup
// ships it already current.

class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.announcement__item'));
    if (this.items.length < 2) return;

    this.index = this.items.findIndex((item) => item.hasAttribute('data-current'));
    if (this.index < 0) this.index = 0;

    this.controller = new AbortController();
    const { signal } = this.controller;

    this.addEventListener('click', (event) => {
      const button = event.target.closest('.announcement__nav');
      if (!button) return;
      this.stop();
      this.show(this.index + Number(button.dataset.step));
    }, { signal });

    // Hovering or tabbing in holds the current message, so a long one can be
    // read to the end. Leaving resumes only if autoplay was asked for.
    this.addEventListener('pointerenter', () => this.stop(), { signal });
    this.addEventListener('pointerleave', () => this.start(), { signal });
    this.addEventListener('focusin', () => this.stop(), { signal });
    this.addEventListener('focusout', () => this.start(), { signal });

    this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.controller?.abort();
  }

  // State is declarative: data-current decides what shows, so a paused
  // tab or a reduced-motion setting can never leave it half open.
  show(next) {
    const total = this.items.length;
    const target = ((next % total) + total) % total;
    this.items[this.index].removeAttribute('data-current');
    this.items[target].setAttribute('data-current', '');
    this.index = target;
  }

  start() {
    const seconds = Number(this.dataset.autoplay);
    if (!seconds) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.stop();
    this.timer = setInterval(() => this.show(this.index + 1), seconds * 1000);
  }

  stop() {
    clearInterval(this.timer);
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}
